import React, { useEffect, useMemo, useState } from "react";

import Box from "components/common/Box";
import Text from "components/common/Text";
import Avatar from "components/common/Avatar";
import IconButton from "components/common/IconButton";
import Button from "components/common/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import MarketPulseAudioPlayer from "./MarketPulseAudioPlayer";
import MarketPulseImageViewer from "./MarketPulseImageViewer";
import MarketPulseToggleButton from "./MarketPulseToggleButton";
import { marketPulseTheme as theme } from "./marketPulseTheme";
import {
  buildPulseSubtitle,
  extractAnalystProfile,
  normalizeMarketPulseResponse,
} from "utils/normalizeMarketPulse";

const DEFAULT_PROFILE = {
  name: "Sreebhashyam Sriniwas  CMT",
  designation: "CIO | JARVIS INVEST",
  avatarUrl: "",
  bio: "Having three decades of Global Markets experience. Forex, forex swaps, Equity Bonds, Commodities, and Derivatives - Futures, Options and OCC. Worked in Banks like Scotia, Bank Muscat, Bank Dhofar, Standard Chartered. Passion in the market for market Imagine Faculty in many programs and a regular contributor to Global Market Newsletter.",
};

const DEFAULT_DISCLAIMER =
  "*Jarvis uses trading view for this chart a global platform offering market data, stock screeners financial news and other comprehensive analytical tools.";

const glassPanelSx = {
  backgroundImage: theme.glassPanelGradient,
  backdropFilter: theme.glassBackdrop,
  borderRadius: theme.glassPanelRadius,
};

const shellSx = {
  width: "100%",
  borderRadius: theme.cardRadius,
  backgroundImage: theme.cardGradient,
  border: theme.cardBorder,
  boxShadow: theme.cardShadow,
  backdropFilter: theme.cardBackdrop,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 120,
  py: 3,
  px: 2,
};

const MarketPulseCard = ({
  response = null,
  loading = false,
  error = null,
  onRetry,
  profile: profileProp,
  title = "9am Market Pulse",
  disclaimer = DEFAULT_DISCLAIMER,
  defaultExpanded = false,
  onExpandedChange,
}) => {
  const pulses = useMemo(
    () => normalizeMarketPulseResponse(response),
    [response],
  );
  const subtitle = useMemo(() => buildPulseSubtitle(pulses), [pulses]);
  const profile = useMemo(() => {
    const fromApi = extractAnalystProfile(response);
    return {
      ...DEFAULT_PROFILE,
      ...(fromApi || {}),
      ...(profileProp || {}),
    };
  }, [response, profileProp]);

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [collapseOverflow, setCollapseOverflow] = useState(
    defaultExpanded ? "visible" : "hidden",
  );
  const [activePulseIndex, setActivePulseIndex] = useState(0);
  const [activeAudioIndex, setActiveAudioIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const activePulse = pulses[activePulseIndex];
  const hasMultiplePulses = pulses.length > 1;
  const COLLAPSE_MS = 480;

  useEffect(() => {
    setActivePulseIndex(0);
  }, [response]);

  useEffect(() => {
    setActiveAudioIndex(0);
    setActiveImageIndex(0);
  }, [activePulseIndex]);

  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  if (loading && !pulses.length) {
    return (
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <Box sx={shellSx}>
          <CircularProgress size={28} sx={{ color: "#fff" }} />
        </Box>
      </Box>
    );
  }

  if (error && !pulses.length) {
    return (
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <Box sx={{ ...shellSx, flexDirection: "column", gap: 1.5 }}>
          <Text
            sx={{
              color: "#fff",
              fontWeight: 600,
              fontSize: 14,
              textAlign: "center",
            }}
          >
            Market Pulse unavailable
          </Text>
          {onRetry ? (
            <Button
              variant="outlined"
              onClick={onRetry}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              Retry
            </Button>
          ) : null}
        </Box>
      </Box>
    );
  }

  if (!pulses.length) {
    return null;
  }

  const handlePulseChange = (direction) => {
    setActivePulseIndex((current) => {
      if (direction === "next" && current < pulses.length - 1) {
        return current + 1;
      }
      if (direction === "prev" && current > 0) {
        return current - 1;
      }
      return current;
    });
  };

  const hasPreviousPulse = activePulseIndex > 0;
  const hasNextPulse = activePulseIndex < pulses.length - 1;

  const sessionLabel = activePulse.session
    ? `${activePulse.session}${activePulse.session.toLowerCase().includes("india") ? "" : " INDIA"}`
    : "Pre-Markets INDIA";

  const toggleExpand = () => setIsExpanded((prev) => !prev);
  const collapsedButtonHeight = 74;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mb: isExpanded ? 8 : 0,
        width: "100%",
        minHeight: isExpanded ? 0 : collapsedButtonHeight,
        transition: `margin-bottom ${COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
      }}
    >
      {/* Keep collapsed pill mounted — fade/slide instead of hard swap */}
      <Box
        aria-hidden={isExpanded}
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          maxHeight: isExpanded ? 0 : 74,
          opacity: isExpanded ? 0 : 1,
          overflow: "hidden",
          pointerEvents: isExpanded ? "none" : "auto",
          transition: `max-height ${COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${
            isExpanded ? 180 : 280
          }ms ease ${isExpanded ? "0ms" : "160ms"}`,
        }}
      >
        <MarketPulseToggleButton
          title={title}
          subtitle={subtitle}
          isExpanded={isExpanded}
          onClick={toggleExpand}
          placement="top"
        />
      </Box>

      <Collapse
        in={isExpanded}
        timeout={COLLAPSE_MS}
        onEnter={() => setCollapseOverflow("hidden")}
        onEntered={() => setCollapseOverflow("visible")}
        onExit={() => setCollapseOverflow("hidden")}
        onExited={() => setCollapseOverflow("hidden")}
        sx={{
          width: "100%",
          overflow: collapseOverflow,
          "& .MuiCollapse-wrapper": { overflow: collapseOverflow },
          "& .MuiCollapse-wrapperInner": { overflow: collapseOverflow },
        }}
      >
        <Box
          sx={{
            width: "100%",
            borderRadius: theme.cardRadius,
            border: theme.cardBorder,
            backgroundImage: theme.cardGradient,
            backdropFilter: theme.cardBackdrop,
            boxShadow: theme.cardShadow,
            position: "relative",
            overflow: "visible",
            pb: { xs: 5.5, sm: 6 },
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 2.5, md: 3 },
              pt: { xs: 2.5, sm: 3 },
              pb: { xs: 2, sm: 2.5 },
              position: "relative",
            }}
          >
            {hasMultiplePulses ? (
              <>
                {hasPreviousPulse ? (
                  <IconButton
                    onClick={() => handlePulseChange("prev")}
                    aria-label="Previous market pulse"
                    sx={{
                      position: "absolute",
                      left: { xs: 4, sm: 8 },
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 4,
                      color: theme.textPrimary,
                      backgroundColor: theme.navBtnBg,
                      width: 34,
                      height: 34,
                      backdropFilter: "blur(4px)",
                      "&:hover": { backgroundColor: theme.navBtnHover },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                ) : null}
                {hasNextPulse ? (
                  <IconButton
                    onClick={() => handlePulseChange("next")}
                    aria-label="Next market pulse"
                    sx={{
                      position: "absolute",
                      right: { xs: 4, sm: 8 },
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 4,
                      color: theme.textPrimary,
                      backgroundColor: theme.navBtnBg,
                      width: 34,
                      height: 34,
                      backdropFilter: "blur(4px)",
                      "&:hover": { backgroundColor: theme.navBtnHover },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                ) : null}
                <Text
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: hasMultiplePulses ? 48 : 16,
                    color: theme.textMuted,
                    fontSize: "11px",
                    zIndex: 4,
                  }}
                >
                  {activePulseIndex + 1} / {pulses.length}
                </Text>
              </>
            ) : null}

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 2, md: 3 },
                alignItems: "stretch",
                px: { xs: 0, md: hasMultiplePulses ? 3 : 0 },
              }}
            >
              {/* Profile panel — Figma left card */}
              <Box
                sx={{
                  ...glassPanelSx,
                  px: { xs: 2, md: 2 },
                  py: { xs: 2.5, md: 3 },
                  width: { xs: "100%", md: 351 },
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    src={profile.avatarUrl}
                    title={profile.name}
                    sx={{
                      width: 42,
                      height: 42,
                      backgroundColor: "rgba(255,255,255,0.22)",
                      flexShrink: 0,
                    }}
                    text_sx={{ fontSize: "15px", color: theme.textPrimary }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Text
                      sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 800,
                        fontSize: "17px",
                        lineHeight: 1,
                        letterSpacing: "-0.51px",
                        color: theme.profileName,
                      }}
                    >
                      {profile.name}
                    </Text>
                    <Text
                      sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: 1,
                        letterSpacing: "-0.48px",
                        color: theme.designation,
                      }}
                    >
                      {profile.designation}
                    </Text>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Text
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: "16px",
                      color: theme.textPrimary,
                    }}
                  >
                    Profile
                  </Text>
                  <Text
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 400,
                      fontSize: "15px",
                      lineHeight: 1.55,
                      color: theme.textPrimary,
                    }}
                  >
                    {profile.bio}
                  </Text>
                </Box>
              </Box>

              <Box
                sx={{
                  ...glassPanelSx,
                  flex: 1,
                  minWidth: 0,
                  position: "relative",
                  overflow: "visible",
                  px: { xs: 1.5, sm: 1.5 },
                  pt: { xs: 2, sm: 2.25 },
                  pb: { xs: 2, sm: 2.25 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      height: 37,
                      px: 2,
                      borderRadius: "14px",
                      backgroundImage: theme.statusPillBg,
                      border: theme.statusPillBorder,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: theme.statusDot,
                        opacity: 0.7,
                        flexShrink: 0,
                      }}
                    />
                    <Text
                      sx={{
                        fontFamily: "'Inter', 'DM Sans', sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: theme.statusText,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sessionLabel}
                    </Text>
                  </Box>

                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      height: 37,
                      px: 2,
                      borderRadius: "14px",
                      backgroundColor: theme.dateBoxBg,
                      border: theme.dateBoxBorder,
                    }}
                  >
                    <CalendarTodayOutlinedIcon sx={{ color: theme.dateText, fontSize: 16 }} />
                    <Text
                      sx={{
                        fontFamily: "'Inter', 'DM Sans', sans-serif",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: theme.dateText,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {activePulse.date}
                    </Text>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 2, sm: 3.5 },
                    alignItems: "stretch",
                    minHeight: { sm: theme.chartHeight },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 3.5,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundImage: theme.outlookBg,
                        border: theme.outlookBorder,
                        borderRadius: "14px",
                        px: 2,
                        pt: 2,
                        pb: 2,
                        minHeight: 103,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <TrendingUpIcon sx={{ color: theme.outlookTitle, fontSize: 20, mt: 0.25 }} />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                          <Text
                            sx={{
                              fontFamily: "'Inter', 'DM Sans', sans-serif",
                              fontWeight: 600,
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: theme.outlookTitle,
                            }}
                          >
                            Market Outlook
                            {activePulse.market ? ` – ${activePulse.market}` : ""}
                          </Text>
                          <Text
                            sx={{
                              fontFamily: "'Inter', 'DM Sans', sans-serif",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "22.75px",
                              color: theme.textSecondary,
                            }}
                          >
                            {activePulse.description}
                          </Text>
                        </Box>
                      </Box>
                    </Box>

                    <MarketPulseAudioPlayer
                      audios={activePulse.audios}
                      activeIndex={activeAudioIndex}
                      onActiveIndexChange={setActiveAudioIndex}
                    />
                  </Box>

                  {activePulse.images.length > 0 ? (
                    <MarketPulseImageViewer
                      images={activePulse.images}
                      activeIndex={activeImageIndex}
                      onActiveIndexChange={setActiveImageIndex}
                      title={activePulse.market || "Chart"}
                    />
                  ) : null}
                </Box>

                <Text
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 400,
                    fontSize: "10px",
                    lineHeight: 1.35,
                    letterSpacing: "-0.3px",
                    color: theme.disclaimer,
                    mt: 0.5,
                  }}
                >
                  {disclaimer}
                </Text>
              </Box>
            </Box>
          </Box>

          {/* Figma 6776:47955 — centered on outer card, not right panel */}
          <MarketPulseToggleButton
            title={title}
            subtitle={subtitle}
            isExpanded={isExpanded}
            onClick={toggleExpand}
            placement="bottom"
            sx={{
              opacity: isExpanded ? 1 : 0,
              transition: `opacity ${isExpanded ? 280 : 120}ms ease ${
                isExpanded ? "180ms" : "0ms"
              }`,
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default MarketPulseCard;
